import { BOARD } from "@grundbesitz/shared";
import type { PublicGameState } from "@grundbesitz/shared";
import { currentRent, fieldActions, hotelSaleNote } from "../../../lib/assets";
import type { AssetAction, AssetActionKind, AssetGroupMeta } from "../../../lib/assets";
import { Button } from "../../ui/Button";
import { Buildings } from "./Buildings";

const HINT_LABEL: Record<AssetActionKind, string> = {
  BUILD: "Bauen",
  SELL_BUILDING: "Verkaufen",
  MORTGAGE: "Hypothek aufnehmen",
  UNMORTGAGE: "Hypothek auflösen",
};

const HATCH =
  "repeating-linear-gradient(135deg, transparent 0 7px, color-mix(in srgb, var(--ink) 9%, transparent) 7px 9px)";

/** One owned property: state, current rent and the actions the engine allows. */
export function PropertyRow({
  game,
  pid,
  field,
  group,
  debtMode,
  hideHint,
  busy,
  onRun,
}: {
  game: PublicGameState;
  pid: string;
  field: number;
  group: AssetGroupMeta;
  debtMode: boolean;
  /** Reasons that the panel already states once at the top (auction, debt) are not repeated per card. */
  hideHint: (message: string) => boolean;
  busy: string | null;
  onRun: (a: AssetAction, field: number) => void;
}) {
  const prop = game.properties[field];
  const actions = fieldActions(game, pid, field);
  const hints = actions.filter((a) => a.error && !hideHint(a.error));
  const hotelNote = prop.buildings === 5 ? hotelSaleNote(game, field) : null;
  const idBase = `asset-${field}`;
  const rent = currentRent(game, field);

  return (
    <li
      className="relative overflow-hidden rounded-xl border border-line bg-surface"
      style={prop.mortgaged ? { backgroundImage: HATCH } : undefined}
    >
      <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1.5" style={{ background: group.color }} />
      <div className="py-3 pl-5 pr-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h4 className="font-display text-base font-semibold leading-tight">{BOARD[field].name}</h4>
          {prop.mortgaged && (
            <span className="rounded-full border border-warn/60 bg-surface px-2 py-0.5 text-xs font-semibold text-warn">
              Belastet
            </span>
          )}
          <Buildings count={prop.buildings} />
          <span className="ml-auto text-right text-sm text-ink-2">
            {prop.mortgaged ? rent : <>Miete <strong className="text-ink">{rent}</strong></>}
          </span>
        </div>

        {actions.length > 0 && (
          <div className="mt-2.5 flex flex-wrap gap-2">
            {actions.map((a) => {
              const emphasise = a.kind === "BUILD" && !debtMode;
              return (
                <Button
                  key={a.kind}
                  variant={emphasise && !a.error ? "primary" : "secondary"}
                  className="flex-1 basis-32"
                  disabled={a.error !== null || busy !== null}
                  aria-describedby={a.error ? `${idBase}-hints` : undefined}
                  title={a.error ?? undefined}
                  onClick={() => onRun(a, field)}
                >
                  {a.label}
                </Button>
              );
            })}
          </div>
        )}

        {(hints.length > 0 || hotelNote) && (
          <div id={`${idBase}-hints`} className="mt-2 space-y-0.5 text-xs text-ink-2">
            {hints.map((a) => (
              <p key={a.kind}>
                <span className="font-semibold text-ink">{HINT_LABEL[a.kind]}:</span> {a.error}
              </p>
            ))}
            {hotelNote && <p>{hotelNote}</p>}
          </div>
        )}
      </div>
    </li>
  );
}
